// UK Inheritance Tax simplified estimator (2026/27 thresholds)
export const NRB_BASE = 325000;
export const RNRB_BASE = 175000;
export const TAPER_THRESHOLD = 2000000;

export const gbp = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

export const pct = (n) =>
  `${((n || 0) * 100).toFixed(1)}%`;

export function calcIHT(inputs) {
  const estate = Math.max(0, Number(inputs.estateValue) || 0);
  const debts = Math.max(0, Number(inputs.debtsFuneral) || 0);
  const gifts = Math.max(0, Number(inputs.gifts7y) || 0);
  const charity = Math.max(0, Number(inputs.charityLegacy) || 0);
  const homeValue = Number(inputs.homeValue) || 0;
  const gy = Math.min(7, Math.max(0, Number(inputs.giftYears) || 0));
  const spouseNrb = Number(inputs.spouseNrbUnused) || 0;
  const spouseRnrb = Number(inputs.spouseRnrbUnused) || 0;
  const homeToDescendants = !!inputs.homeToDescendants;

  // Step 1 - Net estate
  const netEstate = Math.max(0, estate - debts);

  // Step 2 - NRB
  const nrb = NRB_BASE * (1 + spouseNrb);

  // Step 3 - RNRB
  let rnrbMax = 0;
  let rnrb = 0;
  if (homeToDescendants) {
    rnrbMax = RNRB_BASE * (1 + spouseRnrb);
    rnrb = homeValue > 0 ? Math.min(rnrbMax, homeValue) : rnrbMax;
  }

  // Step 4 - RNRB taper
  let rnrbTapered = false;
  let taper = 0;
  if (estate > TAPER_THRESHOLD && rnrb > 0) {
    const excess = estate - TAPER_THRESHOLD;
    taper = Math.floor(excess / 2);
    const before = rnrb;
    rnrb = Math.max(0, rnrb - taper);
    rnrbTapered = rnrb < before;
  }

  // Step 5 - Charity reduced-rate test
  const baseline = Math.max(0, netEstate - nrb - rnrb);
  let rate = 0.4;
  let charityRateApplied = false;
  if (baseline > 0 && charity >= 0.1 * baseline) {
    rate = 0.36;
    charityRateApplied = true;
  }

  // Step 6 - Taxable estate (main)
  const taxableEstate = Math.max(0, netEstate - nrb - rnrb - charity);
  const ihtMain = taxableEstate * rate;

  // Step 7 - Gifts in last 7 years
  const nrbUsedByEstate = Math.min(nrb, netEstate);
  const nrbRemainingForGifts = Math.max(0, nrb - nrbUsedByEstate);
  const giftsChargeable = Math.max(0, gifts - nrbRemainingForGifts);
  let giftRate;
  if (gy < 3) giftRate = 0.4;
  else if (gy < 4) giftRate = 0.32;
  else if (gy < 5) giftRate = 0.24;
  else if (gy < 6) giftRate = 0.16;
  else if (gy < 7) giftRate = 0.08;
  else giftRate = 0;
  const ihtGifts = giftsChargeable * giftRate;

  // Step 8 - Totals
  const ihtDue = ihtMain + ihtGifts;
  const effectiveRate = estate > 0 ? ihtDue / estate : 0;
  const netToHeirs = Math.max(0, estate - debts - charity - ihtDue);

  // Risk band (Low / Medium / High likelihood of IHT)
  const allowances = nrb + rnrb;
  let risk = "low";
  if (ihtDue > 0) {
    risk = effectiveRate >= 0.1 ? "high" : "medium";
  } else if (netEstate > 0.9 * allowances && allowances > 0) {
    risk = "medium";
  }

  // Plain-English summary
  let summary;
  if (ihtDue <= 0) {
    summary =
      "Good news — in this simplified estimate your estate sits within your available tax-free allowances, so no Inheritance Tax is due.";
  } else if (charityRateApplied) {
    summary =
      "Your estate is above the combined tax-free allowances, so some IHT is likely. Your charitable giving of 10%+ has reduced the rate to 36%.";
  } else {
    summary =
      "Your estate is above the combined tax-free allowances, so some Inheritance Tax is likely to be due in this simplified estimate.";
  }

  return {
    netEstate,
    nrb,
    rnrb,
    rnrbMax,
    rnrbTapered,
    taper,
    spouseNrbApplied: spouseNrb > 0,
    spouseRnrbApplied: spouseRnrb > 0,
    charity,
    charityRateApplied,
    rate,
    taxableEstate,
    ihtMain,
    giftsChargeable,
    giftRate,
    ihtGifts,
    ihtDue,
    effectiveRate,
    netToHeirs,
    risk,
    summary,
    isLargeEstate: estate > 10000000,
  };
}
